// Who is signed in on this device, from GET /api/dining/me (the staff row's
// role is read from the database on every call, so a role change is immediate).
import { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { api } from './api';
import { setDiningZone } from './format';

export const DiningSession = createContext(null);

export function useDiningSession() {
  const ctx = useContext(DiningSession);
  if (!ctx) throw new Error('useDiningSession must be used inside the dining app');
  return ctx;
}

export function useMe() {
  const [me, setMe] = useState(null);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const data = await api.get('/dining/me');
      // the restaurant's zone, once per session: every displayed time is formatted on it
      setDiningZone(data?.timezone);
      setMe(data);
      setError(null);
    } catch (e) {
      setError(e);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);
  return { me, error, loading, reload: load };
}

// The screens this person works on, in nav order (role-aware, from the catalog's permissions).
export function screensFor(me) {
  const perms = new Set(me?.permissions || []);
  const role = me?.role;
  const out = [];
  if (perms.has('tables.read')) out.push({ key: 'floor', label: 'Tables', path: 'floor' });
  if (perms.has('kds.read') && (perms.has('kds.write') || ['kitchen', 'expo', 'manager', 'owner'].includes(role))) {
    out.push({ key: 'kds', label: 'Kitchen', path: 'kds' });
  }
  if (perms.has('kds.read') && (perms.has('expo.write') || perms.has('orders.serve'))) {
    out.push({ key: 'expo', label: 'Expo', path: 'expo' });
  }
  if (perms.has('payments.take')) out.push({ key: 'checkout', label: 'Checkout', path: 'checkout' });
  if (perms.has('chat.reply')) out.push({ key: 'chat', label: 'Guest chat', path: 'chat' });
  return out;
}

export function homeFor(me) {
  const role = me?.role;
  const screens = screensFor(me);
  const has = (k) => screens.some((s) => s.key === k);
  if (role === 'kitchen' && has('kds')) return 'kds';
  if (role === 'expo' && has('expo')) return 'expo';
  if (role === 'cashier' && has('checkout')) return 'checkout';
  return screens[0]?.path || null;
}

export function usePerms(me) {
  return useMemo(() => {
    const perms = new Set(me?.permissions || []);
    const approval = new Set(me?.with_approval || []);
    const actions = new Set(me?.actions || []);
    const code = new Set(me?.code_required || []);
    return {
      can: (p) => perms.has(p),
      // the role may do it (directly, or with an approval code when the owner switches it on)
      canWithApproval: (p) => perms.has(p) || approval.has(p),
      // an approvable action (permission_catalog.APPROVABLE_ACTIONS): may I do it at all, and is a code asked now
      may: (action) => actions.has(action),
      needsCode: (action) => code.has(action),
    };
  }, [me]);
}

export const ROLE_LABEL = {
  owner: 'Owner', manager: 'Manager', host: 'Host', waiter: 'Server', cashier: 'Cashier',
  kitchen: 'Kitchen', expo: 'Expo', supervisor: 'Supervisor',
};

export function signOutStaff() {
  try {
    localStorage.removeItem('pos_token');
    localStorage.removeItem('pos_user');
    sessionStorage.removeItem('pos_token');
  } catch (e) { /* storage blocked */ }
}

// On pos.<domain> the register's own PIN sign-in is /login; elsewhere the staff PIN door.
export function signInHref(next) {
  const host = window.location.hostname;
  const pos = host.startsWith('pos.');
  const base = pos ? '/login' : '/staff/login';
  return `${base}?next=${encodeURIComponent(next || '/dining')}`;
}
